/*
	Handles New and Edit matter functionality.  props.editMode determines which.
*/

import React, { Component, useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useBlocker } from "react-router-dom";

import {
	getPermissionGroupDetails,
	createPermissionGroupV2,
	unmountEditPermissionGroup,
	updatePermissionGroupDetails,
} from "../actions/permissionsActions";
import { openUnsavedChangesDialog } from "../actions/globalEditDialogActions";

import { LabeledTextInput } from "./TextInput";
import UsersDropDownSelect from "./UsersDropDownSelect";
import { IconUsers } from "@tabler/icons-react";
import GroupRights from "./GroupRights";
import { ButtonWithIcon } from "./ButtonWithIcon";
import { LexWorkplaceCheckIcon, LexWorkplaceXIcon } from "./LexWorkplaceIcons";

const initialState = {
	name: "",
	groupMembers: [],
	groupRights: {},
};
const userOptionsMap = (user) => ({
	...user,
	value: user.sub,
	label: user.displayName,
});

function NavigationBlocker({ hasUnsavedChanges }) {
	const dispatch = useDispatch();
	const blocker = useBlocker(hasUnsavedChanges);
	const proceededRef = useRef(false);

	useEffect(() => {
		if (blocker.state === "blocked") {
			proceededRef.current = false;
			dispatch(
				openUnsavedChangesDialog(
					() => {
						proceededRef.current = true;
						blocker.proceed();
					},
					() => {
						if (!proceededRef.current) {
							blocker.reset();
						}
					}
				)
			);
		}
	}, [blocker, dispatch]);

	return null;
}

const mapUsersKeepMeFixed = (users, sub, isFirmAdminGroup) => {
	//the isFixed property hides the x button next to a dropdown list item
	return users.map((user) =>
		isFirmAdminGroup && user.sub === sub
			? { ...userOptionsMap(user), isFixed: true }
			: userOptionsMap(user)
	);
};

class NewEditPermissionsGroupV2 extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			name: "",
			groupMembers: [],
			groupRights: {},
			hasUnsavedChanges: false,
			initialState: null,
		};
		this.handleChange = this.handleChange.bind(this);
		this.handleRightChange = this.handleRightChange.bind(this);
		this.handleCancelClick = this.handleCancelClick.bind(this);
		this.checkForUnsavedChanges = this.checkForUnsavedChanges.bind(this);
	}

	static getDerivedStateFromProps(props, state) {
		if (state.loading && !props.loading) {
			if (props.editMode) {
				const newState = {
					loading: false,
					name: props.focusedPermissionGroup?.name,
					groupMembers: props.groupMembers
						? mapUsersKeepMeFixed(
								props.groupMembers,
								props.cognitoSub,
								props.isFirmAdminGroup
							)
						: [],
					groupRights: {
						...props.groupPermissionFeatures,
					},
				};
				return {
					...newState,
					initialState: {
						name: newState.name,
						groupMembers: newState.groupMembers,
						groupRights: newState.groupRights,
					},
					hasUnsavedChanges: false,
				};
			} else {
				const newState = {
					...initialState,
					loading: false,
					groupRights: {
						...props.groupPermissionFeatures,
					},
				};
				return {
					...newState,
					initialState: {
						name: newState.name,
						groupMembers: newState.groupMembers,
						groupRights: newState.groupRights,
					},
					hasUnsavedChanges: false,
				};
			}
		} else {
			return null;
		}
	}

	componentDidMount() {
		if (this.props.createNew) {
			console.log("new group");
			this.props.getPermissionGroupDetails();
		} else if (this.props.editMode) {
			try {
				const groupID = parseInt(this.props.match.params.groupID, 10);
				this.props.getPermissionGroupDetails(groupID);
			} catch (error) {
				console.error("Error fetching permission group:", error);
				this.setState({
					loading: false,
					error: "Failed to load permission group.",
				});
			}
		}

		// Add beforeunload listener to warn about unsaved changes when closing/refreshing browser
		this.beforeUnloadHandler = (e) => {
			if (this.state.hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = ""; // Chrome requires returnValue to be set
				return ""; // Some browsers display this message
			}
		};
		window.addEventListener("beforeunload", this.beforeUnloadHandler);
	}

	handleChange(key, value) {
		this.setState({ [key]: value }, this.checkForUnsavedChanges);
	}
	handleRightChange(rightKey, value) {
		this.setState(
			{
				groupRights: {
					...this.state.groupRights,
					[rightKey]: value,
				},
			},
			this.checkForUnsavedChanges
		);
	}

	checkForUnsavedChanges() {
		if (!this.state.initialState) {
			return;
		}

		const { initialState } = this.state;

		// Compare name
		const nameChanged = this.state.name !== initialState.name;

		// Compare groupMembers (array of objects with value property)
		const currentMemberIds =
			this.state.groupMembers?.map((m) => m.value).sort() || [];
		const initialMemberIds =
			initialState.groupMembers?.map((m) => m.value).sort() || [];
		const membersChanged =
			JSON.stringify(currentMemberIds) !==
			JSON.stringify(initialMemberIds);

		// Compare groupRights (object with boolean values)
		const rightsChanged =
			JSON.stringify(this.state.groupRights) !==
			JSON.stringify(initialState.groupRights);

		const hasChanges = nameChanged || membersChanged || rightsChanged;

		if (hasChanges !== this.state.hasUnsavedChanges) {
			this.setState({ hasUnsavedChanges: hasChanges });
		}
	}
	submit() {
		const groupData = {
			name: this.state.name,
			groupMembers:
				this.state.groupMembers && this.state.groupMembers.length
					? this.state.groupMembers.map((m) => m.value)
					: [],
			groupPermissionFeatures: this.state.groupRights,
		};

		// Clear unsaved changes flag before submitting to allow navigation after save
		this.setState({ hasUnsavedChanges: false });

		if (this.props.editMode) {
			this.props.updatePermissionGroupDetails({
				groupID: parseInt(this.props.match.params.groupID, 10),
				...groupData,
			});
		} else {
			this.props.createPermissionGroupV2(groupData);
		}
	}

	handleCancelClick() {
		// Clear unsaved changes so the navigation blocker doesn't fire,
		// then navigate away
		this.setState({ hasUnsavedChanges: false }, () => {
			window.safeGoBack(this.props.defaultGoBackLocation);
		});
	}

	componentWillUnmount() {
		// Clean up beforeunload listener
		if (this.beforeUnloadHandler) {
			window.removeEventListener(
				"beforeunload",
				this.beforeUnloadHandler
			);
		}
		this.props.unmountEditPermissionGroup();
	}

	submitValidation() {
		// In edit mode, require both a name and unsaved changes
		if (this.props.editMode) {
			return this.state.name !== "" && this.state.hasUnsavedChanges;
		} else {
			// In create mode, just require a name
			return this.state.name !== "";
		}
	}

	render() {
		if (this.props.loading) {
			return <div />;
		} else {
			return (
				<div
					className="noDefaultMargin"
					style={{ margin: "0 30px 30px 30px" }}
				>
					<NavigationBlocker
						hasUnsavedChanges={this.state.hasUnsavedChanges}
					/>
					<div
						style={{
							height: 64,
							lineHeight: "64px",
							display: "flex",
							flexDirection: "row",
							justifyContent: "space-between",
							gap: 8,
						}}
					>
						<div style={{ minWidth: "fit-content" }}>
							<IconUsers
								size={16}
								style={{
									verticalAlign: "middle",
								}}
							/>
							<span
								style={{
									fontFamily: "MainFont-SemiBold",
									fontSize: 16,
									marginLeft: 8,
								}}
							>
								Permission Group Information
							</span>
						</div>
					</div>
					<div>
						<div
							style={{
								backgroundColor: "var(--lw-light-grey-a)",
								padding: 30,
							}}
						>
							<div style={{ maxWidth: "50vw" }}>
								<LabeledTextInput
									label="Group Name*"
									name="name"
									placeholder="New Group Name"
									defaultValue={
										this.props.editMode
											? this.props.focusedPermissionGroup
													.name
											: null
									}
									onChange={this.handleChange}
									disabled={!this.props.allowEditingGroupName}
								/>
								<div key="membersDropDownSelect">
									{this.props.isAllUsersGroup ? (
										<div>
											Entire Firm is a LexWorkplace system
											group; it cannot be renamed and
											membership cannot be modified. New
											users to your firm will
											automatically be included in this
											group.
										</div>
									) : (
										<UsersDropDownSelect
											label="Group Members"
											isMulti
											name="groupMembers"
											isAsync
											isClearable
											value={this.state.groupMembers}
											defaultValue={
												this.props.editMode &&
												this.props
													.focusedPermissionGroup
													.members
													? mapUsersKeepMeFixed(
															this.props
																.focusedPermissionGroup
																.members,
															this.props
																.cognitoSub,
															this.props
																.isFirmAdminGroup
														)
													: []
											}
											options={mapUsersKeepMeFixed(
												this.props.users,
												this.props.cognitoSub,
												this.props.isFirmAdminGroup
											)}
											handler={this.handleChange}
										/>
									)}
								</div>
							</div>
						</div>
						<div style={{ maxWidth: "53vw", margin: "30px 0" }}>
							<GroupRights
								rights={this.state.groupRights}
								onRightChange={this.handleRightChange}
								isFirmAdminGroup={this.props.isFirmAdminGroup}
								tenantFeatures={this.props.tenantFeatures}
							/>
						</div>
						<div
							style={{
								display: "flex",
							}}
						>
							<ButtonWithIcon
								handler={this.handleCancelClick}
								buttonText={"Cancel"}
								iconComponent={<LexWorkplaceXIcon />}
								style={{ marginRight: 8 }}
							/>
							<ButtonWithIcon
								handler={() => {
									this.submit();
								}}
								disabledHoverText={""}
								buttonText={
									this.props.editMode
										? "Update Group"
										: "Create Group"
								}
								className="blue"
								disabled={!this.submitValidation()}
								iconComponent={<LexWorkplaceCheckIcon />}
							/>
						</div>
					</div>
				</div>
			);
		}
	}
}
const mapStateToProps = (state, ownProps) => {
	const focusedPermissionGroup = ownProps.editMode
		? state.permissions.editGroupDetails.group
		: false;
	return {
		loading: state.permissions.newEditPermissionsLoadingV2,
		focusedPermissionGroup: ownProps.editMode
			? focusedPermissionGroup
			: false,
		users: state.permissions.users,
		cognitoSub: state.user.cognitoUser.attributes.sub,
		isFirmAdminGroup: focusedPermissionGroup
			? focusedPermissionGroup.type === "FIRM_ADMIN"
			: false,
		isAllUsersGroup: focusedPermissionGroup
			? focusedPermissionGroup.type === "ALL_TENANT_USERS"
			: false,
		allowEditingGroupName: focusedPermissionGroup
			? focusedPermissionGroup.type === "REGULAR"
			: true,
		groupMembers: state.permissions.editGroupDetails?.groupMembers,
		groupPermissionFeatures:
			state.permissions.editGroupDetails?.groupPermissionFeatures,
		tenantFeatures: state.tenant.tenantFeatures,
	};
};
NewEditPermissionsGroupV2.propTypes = {
	createNew: PropTypes.bool,
	editMode: PropTypes.bool,
};

export default connect(mapStateToProps, {
	getPermissionGroupDetails,
	updatePermissionGroupDetails,
	unmountEditPermissionGroup,
	createPermissionGroupV2,
})(NewEditPermissionsGroupV2);
